import React, {lazy, Suspense, useEffect} from 'react';
import FloatButtonWidget from '@assets/components/Preview/FloatButtonWidget';
import {getChatSessionId, getSessionData} from '@scripttag/helpers/sessionHelpers';
import useDynamicWidgetImports from '@assets/hooks/widget/useDynamicWidgetImports';
import useCartDrawerObserver from '@assets/hooks/widget/useCartDrawerObserver';
import {
  getStorageData,
  removeStorageData,
  SESSION_SHOPIFY_KEY,
  SESSION_SHOPIFY_STORAGE_KEY,
  setStorageData
} from '@assets/helpers/storage';
import useSetStaffStatusScripttag from '@assets/hooks/widget/useSetStaffStatusScripttag';

const WidgetFAQ = lazy(() => import('@assets/components/Preview/WidgetFAQ'));
const OnlineStatusListener = lazy(() =>
  import('@assets/components/Preview/MuiComponents/OnlineStatusListener')
);

const {shopId} = window.AVADA_APP_FAQS_STATUS || {};

export default function LiveWidget(props) {
  const {
    postData,
    settings,
    campaigns,
    hasInteracted,
    setOpenWelcomePopup,
    setOpenNewsletterPopup,
    setIsStaffOnline,
    activeCampaigns,
    setActiveCampaigns,
    openWelcomePopup,
    openNewsletterPopup,
    chatInstance,
    isFirstOpen,
    open
  } = props;
  const sessionId = getChatSessionId();

  const {
    notificationMessage,
    setCustomerReading,
    emptyMessageNotification,
    refreshTimeoutNotificationMessage,
    soundPlayer
  } = useDynamicWidgetImports({
    sessionId,
    hasInteracted,
    settings,
    postData,
    setOpenWelcomePopup,
    setOpenNewsletterPopup,
    campaigns,
    activeCampaigns,
    setActiveCampaigns
  });
  useSetStaffStatusScripttag({
    optionOnline: settings.optionOnline,
    setIsStaffOnline: setIsStaffOnline
  });

  const handleTrackInSession = async (keys = [], usageSession = true) => {
    const sessionShopifyStorage = getStorageData(SESSION_SHOPIFY_STORAGE_KEY);
    const sessionShopify = getSessionData(SESSION_SHOPIFY_KEY);

    const shouldUpdateStorage =
      sessionShopifyStorage.sessionId !== sessionShopify ||
      (keys.length && keys.every(key => !sessionShopifyStorage[key])) ||
      !usageSession;
    if (!shouldUpdateStorage) return;

    const dataParams = keys.reduce((accumulator, key) => ({...accumulator, [key]: true}), {});

    const resp = await postData('/tracking/analytics', {shopId, ...dataParams});
    if (resp.success && usageSession) {
      setStorageData(SESSION_SHOPIFY_STORAGE_KEY, {
        ...sessionShopifyStorage,
        ...dataParams
      });
    }
  };

  const clickButtonTrigger = () => {
    if (open) return;
    handleTrackInSession(['engageChatbox']);
  };

  useEffect(() => {
    const sessionShopifyStorage = getStorageData(SESSION_SHOPIFY_STORAGE_KEY);
    const sessionShopify = getSessionData(SESSION_SHOPIFY_KEY);
    if (sessionShopifyStorage.sessionId === sessionShopify) return;
    removeStorageData(SESSION_SHOPIFY_STORAGE_KEY, {});
  }, []);

  useEffect(() => {
    const sessionShopifyData = getSessionData(SESSION_SHOPIFY_KEY);
    const sessionShopifyStorage = getStorageData(SESSION_SHOPIFY_STORAGE_KEY);

    if (!sessionShopifyData || sessionShopifyStorage.sessionId === sessionShopifyData) return;
    setStorageData(SESSION_SHOPIFY_STORAGE_KEY, {sessionId: sessionShopifyData});
  }, []);

  const {isShowWidget} = useCartDrawerObserver();
  if (!isShowWidget) return null;

  return (
    <>
      <Suspense fallback={<div />}>
        {(isFirstOpen || openWelcomePopup || openNewsletterPopup || activeCampaigns.length > 0) &&
          hasInteracted && (
            <WidgetFAQ
              {...{
                ...props,
                setCustomerReading,
                refreshTimeoutNotificationMessage,
                handleTrackInSession
              }}
            />
          )}
        {(isFirstOpen || notificationMessage.countUnread > 0) && hasInteracted && (
          <OnlineStatusListener {...{chatInstance}} />
        )}
      </Suspense>
      <FloatButtonWidget
        {...{...props, notificationMessage, emptyMessageNotification, clickButtonTrigger}}
      />
      <audio
        ref={soundPlayer}
        src="https://cdnapps.avada.io/faq/sounds/noti-sound.mp3"
        autoPlay={false}
        muted={false}
      />
    </>
  );
}
